import { useRef, useState, useEffect } from 'preact/hooks'

import Icon from '@/components/snippets/icon/icon'

import { SearchInput } from 'libs/athos/components/quick-search/SearchInput'
import { QuickSearchDropdown } from 'libs/athos/components/quick-search/QuickSearchDropdown'
import { AthosAutocompleteProvider } from 'libs/athos/fetchers/autocomplete'
import { BeaconAnalyticsProvider } from 'libs/athos/hooks/useBeaconAnalytics'

function QuickSearch({ id = 'quick-search' }) {
  const searchInputRef = useRef()
  const [inputValue, setInputValue] = useState()
  const [query, setQuery] = useState()
  const closeButtonRef = useRef()
  const modalRef = useRef()
  const [redirect, setRedirect] = useState(false)
  const searchUrl =
    (typeof window !== 'undefined' && window.globals?.routes?.search_url) ||
    (typeof window !== 'undefined' && window.Shopify?.routes?.root ? `${window.Shopify.routes.root}search` : '/search')

  const handleClose = (event) => {
    event.preventDefault()
    modalRef.current?.handleHide()
  }

  const handleUpdateValue = (event) => {
    setInputValue(event.target.value)
  }

  const resetInputValue = () => {
    setInputValue('')
    searchInputRef.current.value = ''
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQuery(inputValue)
    }, 400)
    return () => {
      clearTimeout(timeout)
    }
  }, [inputValue])

  useEffect(() => {
    const modalEl = modalRef.current
    if (!modalEl) return

    const focusInput = () => {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus()
      })
    }

    if (modalEl.dataset.ui === 'visible') focusInput()

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-ui') {
          if (modalEl.dataset.ui === 'visible') focusInput()
        }
      }
    })

    observer.observe(modalEl, { attributes: true, attributeFilter: ['data-ui'] })

    return () => {
      observer.disconnect()
    }
  }, [])

  return (
    <x-modal className="group/search flex items-center h-full z-0" data-id={id} data-body-lock ref={modalRef}>
      <div className="flex flex-col fixed inset-tl w-full h-fit z-100 -translate-y-[150%] group-data-visible/search:translate-y-0 transition-all">
        <form action={searchUrl} method="get" className="w-full h-[72px] pl-margin lg:pr-margin border-b border-borders-default bg-secondary">
          <div className="flex lg:grid lg:grid-cols-full-2full-full items-center justify-between lg:gap-x-1 h-full">
            <div className="lg-max:hidden w-fit h-[32px] text-[--logo-colour] children:size-full">
              <svg viewBox="522 0 247 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M676.178 45.3071C704.338 34.1491 721.879 27.7239 768.344 16.5536C743.124 18.203 687.295 21.9941 648.825 29.4225C648.825 29.4225 675.578 44.9624 676.178 45.3071Z" fill="currentColor"/>
                <path d="M588.584 82.2604H625.395C632.689 71.3363 639.945 66.0065 648.394 60.0737C642.002 57.6304 635.572 55.2671 629.042 52.9838C605.532 63.2555 592.458 77.0291 588.59 82.2604" fill="currentColor"/>
                <path d="M595.88 42.5016C563.063 54.115 544.841 66.6639 532.518 82.2038L573.127 82.2715C577.09 76.8187 592.176 61.0818 620.532 50.1392C612.715 47.5236 604.67 45.0126 596.284 42.5939C596.076 42.5447 595.937 42.5139 595.88 42.5077" fill="currentColor"/>
                <path d="M612.738 18.0426H522.805C613.469 34.2595 657.158 55.0984 727.411 84.572C721.404 80.1346 624.575 23.6985 612.738 18.0426Z" fill="currentColor"/>
              </svg>
            </div>
            <div className="grow flex items-center gap-x-0.5 lg:gap-x-0.875 h-[44px] px-1 py-0.25 bg-brand-tertiary rounded-900 overflow-hidden">
              <div className="shrink-0 size-[14px] lg:size-[16px] children:size-full">
                <Icon id="magnifying-glass" />
              </div>
              <SearchInput className="grow" ref={searchInputRef} redirect={redirect} onInput={handleUpdateValue} />
              {inputValue ? (
                <button className="shrink-0 btn btn-text btn-tertiary btn-s w-fit" onClick={resetInputValue} type="reset" aria-label="Clear search">
                  <span className="text-line">
                    Clear
                  </span>
                </button>
              ) : null}
            </div>
            <div className="shrink-0 flex items-center justify-end">
              <button
                className="btn btn-transparent btn-primary btn-m lg-max:btn-square lg:w-fit lg:!px-0"
                data-modal-parent={id}
                data-modal-role="close"
                ref={closeButtonRef}
                onClick={handleClose}
                aria-label="Close modal"
              >
                <div className="size-[13px] lg:size-[10px] children:size-full">
                  <Icon id="close" />
                </div>
                <span className="lg-max:hidden">
                  Close Search
                </span>
              </button>
            </div>
          </div>
        </form>
        <AthosAutocompleteProvider runOnMount={false} resultsPerPage={4}>
          <BeaconAnalyticsProvider type="autocomplete">
            <QuickSearchDropdown query={query} redirect={redirect} setRedirect={setRedirect} />
          </BeaconAnalyticsProvider>
        </AthosAutocompleteProvider>
      </div>
      <button
        className="fixed inset-tl size-full bg-brand-primary/60 opacity-0 group-data-visible/search:opacity-100 pointer-events-none group-data-visible/search:pointer-events-auto cursor-n-resize z-90 transition-all"
        data-modal-parent={id}
        data-modal-role="close"
        aria-label="Close modal"
      ></button>
    </x-modal>
  )
}

export function WrappedQuickSearch({ id }) {
  return (
    <AthosAutocompleteProvider runOnMount={false}>
      <QuickSearch id={id} />
    </AthosAutocompleteProvider>
  )
}
